import type React from "react"

import { useState, useEffect, useRef } from "react"
import { ChevronDown, ChevronRight } from "lucide-react"
import { Logo } from "./logo"
import { getMenuItemsForRole } from "./sidebar-configs"
import { PortalSwitcher } from "./portal-switcher"
import { TenantAppRoleSwitcher } from "./tenant-app-role-switcher"
import { useIsInsideAppShellChrome } from "./app-shell-chrome-context"
import { useModuleContext } from "@edemics/contexts/module-provider"
import { useRouterAdapter } from "./router-adapter"

interface SidebarProps {
  collapsed?: boolean
  onToggle?: () => void
  /**
   * Optional role override. If provided, drives the menu from
   * getMenuItemsForRole(role) instead of the localStorage lookup.
   * Ported super-admin pages pass role="super-admin" explicitly so the
   * menu is deterministic without leaning on client-side state — kept
   * for verbatim parity with the legacy Next call sites.
   */
  role?: string
  // ---- Legacy prop aliases from academics-page ports ---------------------
  // Academics pages ported from the Next.js source pass a mix of legacy prop
  // shapes ({ isCollapsed, onToggle }), ({ onCollapsedChange }), and
  // ({ configName, collapsed, onToggle }). None of the extra props alter
  // the sidebar's rendering (the AppShell owns collapse state on this
  // route), but declaring them lets the ported call sites compile without
  // touching page JSX.
  isCollapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
  configName?: string
}

interface MenuItem {
  title: string
  icon: React.ReactNode
  path?: string
  badge?: string | number
  submenu?: MenuItem[]
  expanded?: boolean
  moduleKey?: string
}

/**
 * Derive a submodule catalog key from a sidebar path.
 *   /hrms/payroll           → "hrms.payroll"
 *   /accounting/journal-entry → "accounting.journal-entry"
 *   /wallet-management/transactions → "wallet-management.transactions"
 * Returns undefined for paths that don't have a submodule shape
 * (single segment, empty, hash-only), in which case the caller
 * treats the item as always-visible.
 */
function deriveSubmoduleKey(path?: string): string | undefined {
  if (!path) return undefined
  const trimmed = path.replace(/^\/+/, "").replace(/\?.*$/, "").replace(/#.*$/, "")
  const parts = trimmed.split("/").filter(Boolean)
  // Need at least [module, submodule] to form a dotted key.
  if (parts.length < 2) return undefined
  // Only the first two segments — deeper paths are just sub-URLs
  // under the same submodule (e.g. /hrms/payroll/settings still
  // resolves to `hrms.payroll`).
  return `${parts[0]}.${parts[1]}`
}

export function Sidebar({ collapsed: propCollapsed = false, onToggle, role }: SidebarProps) {
  // Suppress double render when a page component renders <Sidebar> inside
  // AppShell's Outlet (which already provides its own sidebar). AppShell's
  // Sidebar is rendered OUTSIDE the provider so it still renders normally.
  if (useIsInsideAppShellChrome()) return null

  const { Link, usePathname } = useRouterAdapter()
  const pathname = usePathname()
  const { isModuleEnabled } = useModuleContext()
  const [collapsed, setCollapsed] = useState(propCollapsed)
  const [isHovering, setIsHovering] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    setCollapsed(propCollapsed)
  }, [propCollapsed])

  const toggleCollapse = () => {
    const newCollapsedState = !collapsed
    setCollapsed(newCollapsedState)
    setIsHovering(false) // Reset hover state when toggling
    setShowTooltip(false) // Hide tooltip when toggling
    if (onToggle) {
      onToggle()
    }
  }

  const handleMouseEnter = () => {
    if (collapsed) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
      setIsHovering(true)

      // Show tooltip after a short delay
      tooltipTimeoutRef.current = setTimeout(() => {
        setShowTooltip(true)
      }, 500)
    }
  }

  const handleMouseLeave = () => {
    if (collapsed) {
      // Clear tooltip timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }
      setShowTooltip(false)

      // Return to collapsed state after delay
      hoverTimeoutRef.current = setTimeout(() => {
        setIsHovering(false)
      }, 300)
    }
  }

  const [menuItems, setMenuItems] = useState<MenuItem[]>([])

  // Does the current route live under this item (or one of its submenu entries)?
  const itemMatchesPath = (item: MenuItem): boolean => {
    if (!pathname) return false
    if (item.path && (pathname === item.path || pathname.startsWith(item.path + "/"))) return true
    if (item.submenu) {
      return item.submenu.some((s) => s.path && (pathname === s.path || pathname.startsWith(s.path + "/")))
    }
    return false
  }

  useEffect(() => {
    // Phase S — resilient role resolution. Previously:
    //   const userRole = role || sessionStorage.getItem("userRole") || "admin"
    // When a tenant admin created a user whose primary `role` ended up as
    // a custom role name ("Accountant", "HR Manager", a RoleProfile
    // label...), `getMenuItemsForRole` hit its `default: return []` and
    // the sidebar rendered empty — the user could sign in but had no
    // way to navigate.
    //
    // Resolution order:
    //   1. Explicit `role` prop from the caller
    //   2. sessionStorage.userRole (what the login response stored)
    //   3. First known app role in sessionStorage.user.appRoles[]
    //      (the multi-role array the BE returns for every tenant user)
    //   4. Hard default to "admin"
    // We accept each candidate only if `getMenuItemsForRole` would
    // actually return a non-empty menu for it, so an unknown primary
    // string gracefully falls through to a valid portal.
    const KNOWN_APP_ROLES = ["admin", "teacher", "parent", "student", "alumni", "super-admin"]
    const normalize = (r: string | null | undefined) =>
      (r || "").toLowerCase().trim()
    const readStoredAppRoles = (): string[] => {
      try {
        const raw = sessionStorage.getItem("user")
        if (!raw) return []
        const parsed = JSON.parse(raw)
        const arr = Array.isArray(parsed?.appRoles) ? parsed.appRoles : []
        return arr.map((x: unknown) => String(x))
      } catch {
        return []
      }
    }
    const candidates = [
      role,
      sessionStorage.getItem("userRole"),
      ...readStoredAppRoles(),
      "admin",
    ]
    let roleMenuItems = getMenuItemsForRole("admin")
    for (const candidate of candidates) {
      const normalized = normalize(candidate)
      if (!normalized) continue
      if (!KNOWN_APP_ROLES.includes(normalized)) continue
      const menu = getMenuItemsForRole(normalized)
      if (menu.length > 0) {
        roleMenuItems = menu
        break
      }
    }
    // Filter menu items based on module assignments — top-level AND
    // submenu items. Submenus without an explicit moduleKey get a key
    // auto-derived from their path (`/hrms/payroll` → `hrms.payroll`)
    // so the exhaustive submodule seed from BE #42 gates them without
    // per-entry wiring here.
    const filteredItems = roleMenuItems
      .filter((item) => !item.moduleKey || isModuleEnabled(item.moduleKey))
      .map((item) => {
        if (!item.submenu) return item
        const filteredSubmenu = item.submenu.filter((sub) => {
          const key = sub.moduleKey ?? deriveSubmoduleKey(sub.path)
          return !key || isModuleEnabled(key)
        })
        // If every submenu item is gated off, hide the parent too —
        // no point showing an empty group.
        if (filteredSubmenu.length === 0) return null
        return { ...item, submenu: filteredSubmenu }
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
    // Auto-expand any submenu whose current route matches so children are
    // visible + highlighted immediately on load / navigation.
    const withActiveExpanded = filteredItems.map((item) =>
      item.submenu && itemMatchesPath(item) ? { ...item, expanded: true } : item,
    )
    setMenuItems(withActiveExpanded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModuleEnabled, pathname, role])

  const toggleSubmenu = (index: number) => {
    const updatedMenuItems = [...menuItems]
    updatedMenuItems[index] = {
      ...updatedMenuItems[index],
      expanded: !updatedMenuItems[index].expanded,
    }
    setMenuItems(updatedMenuItems)
  }

  const handleChevronClick = (e: React.MouseEvent, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    toggleSubmenu(index)
  }

  const isExpanded = collapsed && isHovering
  const effectiveCollapsed = collapsed && !isHovering

  return (
    <aside
      className={`h-screen shrink-0 flex flex-col border-r border-divider bg-light-bg dark:bg-dark-bg transition-all duration-300 ${
        effectiveCollapsed ? "w-[70px]" : "w-[260px]"
      } relative`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="p-4 flex items-center justify-between border-b border-divider">
        {effectiveCollapsed ? (
          <div className="flex items-center justify-center w-full">
            <img src="/images/favicon.png" alt="Edemics Favicon" className="w-8 h-8" />
          </div>
        ) : (
          <>
            <Logo collapsed={false} />
            <div className="relative">
              <button
                className="w-6 h-6 rounded-full border border-divider flex items-center justify-center hover:bg-primary-light transition-colors"
                onClick={toggleCollapse}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <div className="w-3 h-3 rounded-full bg-primary"></div>
              </button>

              {showTooltip && collapsed && isHovering && (
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50">
                  Click to expand permanently
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-900"></div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* sidebar-scroll: thin styled scrollbar defined in globals.css
          (plugin-free replacement for the old `scrollbar-thin` class) */}
      <div className="flex-1 overflow-y-auto py-2 sidebar-scroll">
        <div className="mb-4">
          {!effectiveCollapsed && (
            <div className="text-xs uppercase font-semibold mb-2 px-4 text-light-text-secondary dark:text-dark-text-secondary">
              Apps
            </div>
          )}
          <nav>
            <ul className="space-y-1">
              {menuItems.map((item, index) => (
                <li key={item.title}>
                  {item.submenu ? (
                    <div>
                      <div
                        className={`flex items-center gap-3 px-4 py-2 ${
                          // Parent row: full gradient only for an exact hit on
                          // its own path OR when the submenu is collapsed but
                          // we're deep inside it (so the active branch is still
                          // visible). When the submenu is expanded, the active
                          // sub-item already shows the highlight, so keep the
                          // parent visually calm.
                          pathname === item.path
                            ? "bg-gradient-to-r from-[#8C57FF] to-[#5B34C0] text-white"
                            : (!item.expanded && itemMatchesPath(item))
                              ? "bg-gradient-to-r from-[#8C57FF] to-[#5B34C0] text-white"
                              : "hover:bg-primary-light hover:text-primary text-light-text-primary dark:text-[#EFE3FC] dark:text-opacity-90"
                        } transition-colors rounded-r-full cursor-pointer`}
                        onClick={() => {
                          if (!effectiveCollapsed) {
                            toggleSubmenu(index)
                          }
                        }}
                      >
                        <span className="flex-shrink-0">{item.icon}</span>
                        {!effectiveCollapsed && (
                          <>
                            <Link
                              href={item.path || "#"}
                              className="flex-1 text-left"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {item.title}
                            </Link>
                            {item.badge && (
                              <span className="bg-error text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                {item.badge}
                              </span>
                            )}
                            <button
                              onClick={(e) => handleChevronClick(e, index)}
                              className="p-1 hover:bg-white/20 rounded"
                              aria-label={item.expanded ? "Collapse submenu" : "Expand submenu"}
                            >
                              {item.expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                          </>
                        )}
                      </div>
                      {!effectiveCollapsed && item.expanded && item.submenu && (
                        <ul className="mt-1 space-y-1">
                          {item.submenu.map((subItem) => {
                            // Sub-item stays active on nested routes too
                            // (e.g. /accounting/cost-centers/new should keep
                            // "Cost Centers" highlighted, not lose it).
                            const isSubActive =
                              !!subItem.path &&
                              (pathname === subItem.path || pathname.startsWith(subItem.path + "/"))
                            return (
                              <li key={subItem.title}>
                                <Link
                                  href={subItem.path || "#"}
                                  className={`flex items-center gap-3 pl-12 pr-4 py-2 ${
                                    isSubActive
                                      ? "bg-gradient-to-r from-[#8C57FF] to-[#5B34C0] text-white rounded-r-full"
                                      : "hover:bg-primary-light hover:text-primary text-light-text-primary dark:text-[#EFE3FC] dark:text-opacity-90"
                                  } transition-colors`}
                                >
                                  <span className="flex-shrink-0 text-current">{subItem.icon}</span>
                                  <span className="flex-1">{subItem.title}</span>
                                </Link>
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <Link
                      href={item.path || "#"}
                      className={`flex items-center gap-3 px-4 py-2 ${
                        pathname === item.path
                          ? "bg-gradient-to-r from-[#8C57FF] to-[#5B34C0] text-white"
                          : "hover:bg-primary-light hover:text-primary text-light-text-primary dark:text-[#EFE3FC] dark:text-opacity-90"
                      } transition-colors rounded-r-full`}
                    >
                      <span className="flex-shrink-0">{item.icon}</span>
                      {!effectiveCollapsed && (
                        <>
                          <span className="flex-1">{item.title}</span>
                          {item.badge && (
                            <span className="bg-error text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {!effectiveCollapsed && (
          <>
            
          </>
        )}
      </div>

      <div className="mt-auto border-t border-divider sticky bottom-0 bg-light-bg dark:bg-dark-bg py-3 px-4 space-y-2">
        {/*
          Two switchers, mutually exclusive in practice:
          - PortalSwitcher renders only for super-admin (kind='platform',
            role='super-admin') to impersonate into any tenant portal.
          - TenantAppRoleSwitcher renders only for tenant users whose
            user.appRoles.length > 1 — flips between their held app roles
            via POST /auth/switch-role. A tenant user with a single role
            sees neither switcher; the super-admin sees only PortalSwitcher.
        */}
        {!effectiveCollapsed && <PortalSwitcher />}
        {!effectiveCollapsed && <TenantAppRoleSwitcher />}
      </div>
    </aside>
  )
}

// Default-export alias — several pages import this without braces.
export default Sidebar